// API client. Uses fetch with credentials so the CF Access cookie is sent.
// Base URL is configurable via VITE_API_BASE for local dev.
//
// Production behavior: backend is at https://dashboard-api.amplysales.com,
// browser is at https://dashboard.amplysales.com — same Cloudflare Access
// JWT cookie covers both because they share the team domain. CORS on the
// backend explicitly allows the frontend origin.

import type { DashboardState } from './types';

const API_BASE = import.meta.env.VITE_API_BASE || 'https://dashboard-api.amplysales.com';

async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const r = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
  });
  if (r.status === 401) {
    // CF Access is supposed to redirect to login; if we land here it usually
    // means the JWT cookie expired. Reload — CF will re-auth us.
    window.location.reload();
    throw new Error('unauthorized');
  }
  return r;
}

export async function getState(opts?: { fresh?: boolean }): Promise<DashboardState> {
  // fresh=true → bypass the backend's 60s GitHub cache, force a fresh
  // gh pr/issue list. Used by the manual Refresh button.
  const url = opts?.fresh ? '/api/state?fresh=1' : '/api/state';
  const r = await apiFetch(url);
  if (!r.ok) throw new Error(`state fetch failed: ${r.status}`);
  return r.json();
}

export async function getLogTail(n = 100): Promise<string> {
  const r = await apiFetch(`/api/logs/tail?n=${n}`);
  if (!r.ok) throw new Error(`logs fetch failed: ${r.status}`);
  return r.text();
}

export async function postControl(action: 'pause' | 'unpause' | 'scan'): Promise<{ status: number; body: unknown }> {
  const r = await apiFetch(`/api/control/${action}`, { method: 'POST' });
  let body: unknown;
  try { body = await r.json(); } catch { body = await r.text(); }
  return { status: r.status, body };
}
